import { getFirstNameStartingChar } from './nickname';

export const getColorFromString = (str: string): string => {
  // Simple hash function to convert email to a number
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Convert the hash to a color
  const h = hash % 360; // Hue (0-360)
  const s = 70; // Saturation (70%)
  const l = 50; // Lightness (50%)

  return `hsl(${h}, ${s}%, ${l}%)`;
};

export const interpolateHsl = (
  startHue: number,
  startSaturation: number,
  startLightness: number,
  endHue: number,
  endSaturation: number,
  endLightness: number,
  progress: number,
): string => {
  // Handle hue wrapping (e.g., 350° to 10° should go through 0°)
  const hueDiff = endHue - startHue;
  const shortestPath = hueDiff > 180 ? hueDiff - 360 : hueDiff < -180 ? hueDiff + 360 : hueDiff;
  const interpolatedHue = (startHue + shortestPath * progress + 360) % 360;

  // Interpolate saturation and lightness linearly
  const interpolatedSaturation = Math.round(
    startSaturation + (endSaturation - startSaturation) * progress,
  );
  const interpolatedLightness = Math.round(
    startLightness + (endLightness - startLightness) * progress,
  );

  return `hsl(${interpolatedHue}, ${interpolatedSaturation}%, ${interpolatedLightness}%)`;
};

// Generates a deterministic numeric ID from a string
export const generateIdFromString = (str: string): number => {
  return str.split('').reduce((acc, char) => {
    return acc + char.charCodeAt(0) * 37 + 1;
  }, 0);
};

export const generateNumberFromString = (str: string): number => {
  return generateIdFromString(str) % 1000000;
};

export const getColorGoodAgainstWhiteText = (
  colorNum: number,
  colors: number,
  seed?: number,
): string => {
  if (colors < 1) colors = 1; // defaults to one color - avoid divide by zero
  const hue = (colorNum * (360 / colors) + (seed || colors) * 7 * 10) % 360;
  // Neon colors with good contrast against white text
  let saturation = 90; // High saturation for neon effect
  let lightness = 45; // Moderate-dark lightness for readability

  // Fine-tune specific hue ranges for even better neon appearance
  if (hue >= 0 && hue < 60) {
    // Reds and oranges - more vibrant/saturated
    saturation = 100;
    lightness = 42;
  } else if (hue >= 60 && hue < 120) {
    // Yellows and greens - darkened for contrast against white text
    saturation = 100;
    lightness = 20;
  } else if (hue >= 120 && hue < 180) {
    // Cyans and teals - darkened for contrast
    saturation = 95;
    lightness = 32;
  } else if (hue >= 180 && hue < 240) {
    // Blues
    saturation = 95;
    lightness = 28;
  } else if (hue >= 240 && hue < 300) {
    // Magentas and purples
    saturation = 100;
    lightness = 48;
  } else {
    // Rest (pink-reds)
    saturation = 100;
    lightness = 50;
  }

  return `hsl(${hue},${saturation}%,${lightness}%)`;
};

/**
 * Initial label for speaker-diarization avatars. Handles two shapes:
 * - "SPEAKER_NN" → "sN" (drops leading zero; used for unknown speakers)
 * - Any other string → up to 2 uppercase Latin letters taken from the
 *   first character of each whitespace-delimited word
 *
 * For user avatars prefer getAvatarInitial() below, which is
 * nickname-aware and Unicode-friendly.
 */
export const getSpeakerInitials = (label?: string | null): string => {
  if (!label) return '';
  if (label.includes('SPEAKER_')) {
    return 's' + label.split('_')[1].slice(-2).replace(/^0/, '');
  }
  return label
    .split(' ')
    .map((name) => name[0].toUpperCase())
    .filter((char) => /[a-zA-Z]/.test(char))
    .join('')
    .slice(0, 2);
};

type AvatarUser = {
  email?: string | null;
  name?: { nickname?: string | null } | null;
};

/**
 * Single-character avatar initial for a user. Prefers the first
 * name-starting character (\p{L} or \p{N}) of the nickname; falls back
 * to the first name-starting character of the email. Returns '' if
 * neither yields one. Always uppercased (no-op on non-cased scripts).
 */
export const getAvatarInitial = (user?: AvatarUser | null): string => {
  const nickname = user?.name?.nickname ?? '';
  const fromNickname = getFirstNameStartingChar(nickname);
  if (fromNickname) return fromNickname.toUpperCase();
  const email = user?.email ?? '';
  const fromEmail = getFirstNameStartingChar(email);
  return fromEmail ? fromEmail.toUpperCase() : '';
};
